import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { clickableRowProps } from '@/lib/clickableRow';
import { cn } from '@/lib/cn';

interface RowCardProps {
  /** Primary heading line — usually the entity's name or code. */
  title: ReactNode;
  /** Optional inline badge(s) shown next to the title (status pills, etc.). */
  badges?: ReactNode;
  /** Subtitle line beneath the title. */
  subtitle?: ReactNode;
  /** Stacked metadata rows shown below the heading. */
  meta?: ReactNode;
  /** Trailing actions row (icon buttons). Click events are stopped here so the
   *  row's click handler doesn't fire when the user taps an action. */
  actions?: ReactNode;
  /** Activates the card; receives keyboard activation via clickableRowProps. */
  onActivate?: (() => void) | null;
}

/**
 * Mobile-friendly stacked row used in list pages below the `md` breakpoint.
 * Mirrors the desktop table row's keyboard semantics so the same handler can
 * be reused for both layouts.
 */
export function RowCard({ title, badges, subtitle, meta, actions, onActivate }: RowCardProps) {
  const interactive = Boolean(onActivate);
  const rowProps = clickableRowProps(onActivate ?? null);
  return (
    <div
      {...rowProps}
      className={cn(
        'flex items-start gap-3 rounded-lg border border-border bg-card p-3 transition-colors',
        interactive && 'cursor-pointer hover:bg-muted/30',
      )}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground break-words">{title}</span>
          {badges}
        </div>
        {subtitle ? (
          <div className="text-xs text-muted-foreground break-words">{subtitle}</div>
        ) : null}
        {meta ? <div className="space-y-0.5 text-sm text-muted-foreground">{meta}</div> : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      ) : interactive ? (
        <ChevronRight
          className="mt-1 h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden="true"
          focusable="false"
        />
      ) : null}
    </div>
  );
}
